import { useTranslation } from 'react-i18next'
import { Download, Clock, Shield, Brain } from 'lucide-react'
import type { HistoryStats } from '../../types/history'

function StatBadge({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number | string
  icon?: typeof Clock
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
      {Icon && <Icon className="h-4 w-4 text-slate-500 dark:text-slate-400" />}
      <span className="text-sm text-slate-600 dark:text-slate-400">{label}:</span>
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  )
}

interface TimelineHeaderProps {
  stats: HistoryStats
  caseName: string
  onExport?: ((format: string) => void) | undefined
}

export function TimelineHeader({
  stats,
  caseName,
  onExport,
}: TimelineHeaderProps): React.ReactElement {
  const { t } = useTranslation('history')

  return (
    <>
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
          >
            {t('title')}
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            {t('subtitle', { caseName, count: stats.totalEntries })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${
              stats.verificationStatus === 'valid'
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}
          >
            <Shield className="h-4 w-4" />
            {stats.verificationStatus === 'valid' ? t('chainVerified') : t('chainPending')}
          </div>
          <button
            onClick={() => onExport?.('pdf')}
            className="flex items-center gap-2 rounded-lg bg-[var(--accent,#4e5e82)] px-4 py-2 font-medium text-white transition-colors hover:opacity-90"
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <StatBadge label={t('statToday')} value={stats.entriesToday} icon={Clock} />
        <StatBadge label={t('statThisWeek')} value={stats.entriesThisWeek} />
        <StatBadge label={t('statMemories')} value={stats.memoriesCreated} icon={Brain} />
      </div>
    </>
  )
}
